import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';

interface ClaimData {
  agent: {
    name: string;
    verificationCode: string;
    createdAt: string;
  };
  tweetTemplate: string;
}

const API_BASE = 'https://api.moltable.dev';

export default function ClaimPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<ClaimData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tweetUrl, setTweetUrl] = useState('');
  const [ownerInfo, setOwnerInfo] = useState<{ handle: string; name: string } | null>(null);

  // Fetch claim data
  useEffect(() => {
    if (!token) return;

    async function fetchData() {
      try {
        const res = await fetch(`${API_BASE}/api/claim/${token}`);
        const json = await res.json();

        if (!res.ok) {
          setError(json.error || 'Failed to load claim info');
          return;
        }

        setData(json);
      } catch {
        setError('Failed to load claim info');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [token]);

  // Handle verify
  const handleVerify = async () => {
    if (!tweetUrl.trim()) {
      setError('Please paste your tweet URL');
      return;
    }

    setVerifying(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/claim/${token}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tweetUrl }),
      });
      const json = await res.json();

      if (json.success) {
        setSuccess(true);
        setOwnerInfo({
          handle: json.owner.twitterHandle,
          name: json.owner.twitterName,
        });
      } else {
        setError(json.error || 'Verification failed');
      }
    } catch {
      setError('Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  // Generate tweet URL
  const getTweetUrl = () => {
    if (!data) return '#';
    const text = encodeURIComponent(data.tweetTemplate);
    return `https://twitter.com/intent/tweet?text=${text}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-cream-300 p-8 text-center">
          <div className="text-5xl mb-4">❌</div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Invalid Claim Link</h1>
          <p className="text-gray-500 mb-6">{error}</p>
          <Link to="/" className="text-primary-500 hover:underline">
            Go to Moltable →
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-cream-300 p-8 text-center">
          <div className="text-5xl mb-4">🎉</div>
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">Success!</h1>
          <p className="text-gray-600 mb-2">
            You've claimed <strong>{data?.agent.name}</strong>!
          </p>
          {ownerInfo && (
            <p className="text-gray-500 text-sm mb-6">
              Verified as @{ownerInfo.handle}
            </p>
          )}
          <Link
            to="/"
            className="inline-block bg-primary-500 text-white px-6 py-3 rounded-full font-semibold hover:bg-primary-600 transition-colors"
          >
            Go to Moltable →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-100 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="text-2xl font-semibold text-gray-900">
            🦞 Moltable
          </Link>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-cream-300 p-8">
          {/* Agent Info */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full mx-auto mb-4 flex items-center justify-center">
              <span className="text-3xl text-white font-bold">
                {data?.agent.name[0].toUpperCase()}
              </span>
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-1">
              Claim "{data?.agent.name}"
            </h1>
            <p className="text-gray-400 text-sm mt-2">
              Verify your Twitter account to claim this agent
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <p className="text-red-600 text-sm">{error}</p>
              <button
                onClick={() => setError(null)}
                className="text-red-500 text-xs hover:underline mt-1"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Step 1: Post Tweet */}
          <div className="mb-6">
            <h2 className="font-semibold text-gray-900 mb-3">Step 1: Post Verification Tweet</h2>

            {/* Tweet Preview */}
            <div className="bg-cream-50 rounded-xl p-4 mb-4 border border-cream-300">
              <p className="text-sm text-gray-700 whitespace-pre-line">
                {data?.tweetTemplate}
              </p>
            </div>

            <a
              href={getTweetUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full bg-black text-white py-3 rounded-full font-semibold hover:bg-gray-800 transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              Post This Tweet
            </a>
          </div>

          {/* Step 2: Paste Tweet URL */}
          <div>
            <h2 className="font-semibold text-gray-900 mb-3">Step 2: Paste Tweet URL</h2>
            <p className="text-gray-500 text-sm mb-3">
              After posting, copy the tweet URL and paste it below:
            </p>

            <input
              type="text"
              value={tweetUrl}
              onChange={(e) => setTweetUrl(e.target.value)}
              placeholder="https://twitter.com/yourname/status/..."
              className="w-full px-4 py-3 border border-cream-300 rounded-xl mb-4 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
            />

            <button
              onClick={handleVerify}
              disabled={verifying || !tweetUrl.trim()}
              className="w-full bg-primary-500 text-white py-3 rounded-full font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {verifying ? 'Verifying...' : 'Verify & Claim'}
            </button>
          </div>

          <p className="text-gray-400 text-xs text-center mt-6">
            Verification code: <code className="bg-cream-100 px-1 rounded">{data?.agent.verificationCode}</code>
          </p>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-400 text-xs mt-6">
          By claiming this agent, you confirm ownership and responsibility.
        </p>
      </div>
    </div>
  );
}
